import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
    EuiButtonEmpty,
    EuiDescriptionList,
    EuiDescriptionListDescription,
    EuiDescriptionListTitle,
    EuiFlexGroup,
    EuiFlexItem,
    EuiLoadingSpinner,
    EuiTextColor,
    EuiIcon,
} from "@elastic/eui";
import _ from "lodash";
import moment from "moment";
import styled from "styled-components";
import { useIntl, FormattedMessage } from "react-intl";
import AbstractDropdown from "../../../shared/entity/components/AbstractFormField/fields/AbstractDropdown";
import AbstractNumber from "../../../shared/entity/components/AbstractFormField/fields/AbstractNumber";
import TripFieldForm from "../shared/TripFieldForm";
import AbstractSelect from "../../../shared/entity/components/AbstractFormField/fields/AbstractSelect";
import AbstractDate from "../../../shared/entity/components/AbstractFormField/fields/AbstractDate";
import AbstractCheckbox from "../../../shared/entity/components/AbstractFormField/fields/AbstractCheckbox";
import DocumentsModal from "../shared/DocumentsModal";
import ConfirmationModal from "../../../shared/modals/ConfirmationModal";
import AbstractString from "../../../shared/entity/components/AbstractFormField/fields/AbstractString";
import { dateToFormattedFullDateAndTime } from "../../../../utils/dateAndTime";
import { TRADE_WIND } from "../../../../constants/style/colors";
import trackAnalytics from "../../../../trackAnalytics";

export const TripDetailsSectionWrapper = styled.div`
    .trip-details__label {
        text-transform: capitalize;
        margin-bottom: 5px;
    }
    .trip-details__field-confirmed {
        color: ${TRADE_WIND};
        margin-left: 7px;
    }
`;

export default function TripDetailsSection({
    label,
    options = {},
    selectedValue,
    onSelectChange,
    onSave,
    onConfirmingField,
    isEditable,
    pickupName,
    isConfirmed,
    organizationId,
}) {
    const { register, errors, handleSubmit, control } = useForm({
        mode: "onBlur",
    });

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [dropdownValue, setDropdownValue] = useState(() =>
        selectedValue ? [{ label: selectedValue || "" }] : []
    );
    const [isLoading, setIsLoading] = useState(false);
    const [confirmationData, setConfirmationData] = useState(null);
    const [isConfirmingPopoverOpen, setIsConfirmingPopoverOpen] = useState(false);
    const intl = useIntl();

    const successCallBack = () => setIsLoading(false);
    const contractTypeLabel = intl.formatMessage({ id: "trip.contract-type" });

    useEffect(() => {
        setDropdownValue(selectedValue ? [{ label: selectedValue || "" }] : []);
    }, [selectedValue]);

    useEffect(() => {
        // register form hooks for file field
        if (options.type === "attachment") {
            register({ name: options.name });
        }
    }, [register, options.type, options.name]);

    const closePopover = () => {
        setIsPopoverOpen(false);
    };

    const openPopover = () => {
        setIsPopoverOpen(true);
    };

    const openConfirmingPopover = () => {
        setIsConfirmingPopoverOpen(true);
    };

    const onDropdownValueChange = (value) => {
        setDropdownValue(value);
    };
    const saveDropdownValue = () => {
        setIsLoading(true);
        if ((dropdownValue[0] && dropdownValue[0].label !== selectedValue) || !dropdownValue[0]) {
            onSelectChange(
                { [options.name]: dropdownValue[0] ? dropdownValue[0].value : null },
                successCallBack
            );
            trackAnalytics(`trips/id ${options.name}_edit`, {
                [dropdownValue[0].label]: dropdownValue[0].label,
                orgId: organizationId,
            });
        } else {
            successCallBack();
        }
    };

    const handleSubmitConfirmation = (data) => {
        setConfirmationData(data);
    };

    const onSubmit = (data) => {
        setIsLoading(true);
        let dataToSubmit = data;
        if (!_.isNil(data.source)) {
            // TODO: Create enum for source
            data.source === true
                ? (dataToSubmit = { source: "system" })
                : (dataToSubmit = { source: "manual" });
        }

        onSave(
            moment.isMoment(dataToSubmit[options.name])
                ? { [options.name]: dataToSubmit[options.name].locale("en").format() }
                : dataToSubmit,
            () => {
                successCallBack();
                closePopover();
            }
        );
    };

    const renderInput = () => {
        switch (options.type) {
            case "list":
                return (
                    <AbstractDropdown
                        entityOptions={options}
                        onValueChange={onDropdownValueChange}
                        selectedOptions={dropdownValue}
                        options={options.data}
                    />
                );
            case "number":
                return (
                    <AbstractNumber
                        options={options}
                        errors={errors}
                        register={register}
                        entityObject={{ [options.name]: selectedValue }}
                    />
                );
            case "select":
                return (
                    <AbstractSelect
                        options={options}
                        errors={errors}
                        register={register}
                        entityObject={{ [options.name]: selectedValue }}
                    />
                );
            case "dateAndTime":
                return (
                    <AbstractDate
                        options={options}
                        errors={errors}
                        register={register}
                        control={control}
                        entityObject={{
                            [options.name]: selectedValue ? moment(selectedValue) : null,
                        }}
                    />
                );
            case "checkbox":
                return (
                    <AbstractCheckbox
                        options={options}
                        isChecked={
                            selectedValue && _.isString(selectedValue)
                                ? selectedValue.toLocaleLowerCase() === "system"
                                : selectedValue
                        }
                        register={register}
                    />
                );
            case "string":
                return (
                    <AbstractString
                        options={options}
                        errors={errors}
                        entityObject={{
                            [options.name]: selectedValue,
                        }}
                        register={register}
                    />
                );
            default:
                break;
        }
    };

    return (
        <TripDetailsSectionWrapper>
            <EuiDescriptionList textStyle="reverse">
                <EuiDescriptionListTitle>
                    <EuiTextColor
                        color="subdued"
                        className="trip-details__label"
                    >
                        {label}
                    </EuiTextColor>
                    {isConfirmed && (
                        <EuiIcon
                            type="checkInCircleFilled"
                            className="trip-details__field-confirmed"
                        />
                    )}
                </EuiDescriptionListTitle>
                <EuiDescriptionListDescription>
                    <EuiFlexGroup
                        gutterSize="xs"
                        alignItems="center"
                        justifyContent="flexStart"
                        responsive={false}
                    >
                        <EuiFlexItem grow={false}>
                            <b>
                                {typeof selectedValue === "boolean"
                                    ? selectedValue
                                        ? "Yes"
                                        : "No"
                                    : _.isNumber(selectedValue) ||
                                      (!_.isNumber(selectedValue) && !!selectedValue)
                                    ? options.type === "attachment"
                                        ? selectedValue.length
                                        : options.type === "dateAndTime" &&
                                          typeof selectedValue === "string"
                                        ? dateToFormattedFullDateAndTime(selectedValue)
                                        : options.format
                                        ? _.startCase(selectedValue)
                                        : _.startCase(selectedValue) && label === contractTypeLabel
                                        ? intl.formatMessage({
                                              id: `contractType.${_.kebabCase(selectedValue)}`,
                                          })
                                        : selectedValue
                                    : "N/A"}
                            </b>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            {isLoading ? (
                                <EuiLoadingSpinner
                                    style={{ marginLeft: 10 }}
                                    size="m"
                                />
                            ) : options.type === "attachment" ? (
                                <EuiButtonEmpty
                                    size="xs"
                                    onClick={openPopover}
                                >
                                    <b>
                                        <FormattedMessage id="action.view" />
                                    </b>
                                </EuiButtonEmpty>
                            ) : (
                                isEditable && (
                                    <TripFieldForm
                                        openPopover={openPopover}
                                        openConfirmingPopover={openConfirmingPopover}
                                        closePopover={closePopover}
                                        isPopoverOpen={isPopoverOpen}
                                        isLoading={isLoading}
                                        input={renderInput()}
                                        onSubmit={
                                            options.confirmation
                                                ? handleSubmit(handleSubmitConfirmation)
                                                : options.type === "list"
                                                ? saveDropdownValue
                                                : handleSubmit(onSubmit)
                                        }
                                        fullWidth={options.type === "attachment"}
                                        isConfirmable={!isConfirmed && options.confirmable}
                                    />
                                )
                            )}
                        </EuiFlexItem>
                    </EuiFlexGroup>
                </EuiDescriptionListDescription>
            </EuiDescriptionList>

            {options.type === "attachment" && isPopoverOpen && (
                <DocumentsModal
                    onClose={closePopover}
                    onSubmit={onSubmit}
                    onDelete={(attachment) => {
                        setIsLoading(true);
                        onSave(attachment, successCallBack);
                    }}
                    defaultValue={selectedValue}
                    isLoading={isLoading}
                    isEditable={isEditable}
                    title={pickupName}
                />
            )}

            {isConfirmingPopoverOpen && (
                <ConfirmationModal
                    title={intl.formatMessage({
                        id: `trip-form.confirm-${options.name}-confirmation-title`,
                    })}
                    content={intl.formatMessage(
                        {
                            id: `trip-form.confirm-${options.name}-confirmation-content`,
                        },
                        { name: selectedValue }
                    )}
                    onConfirm={() => {
                        setIsLoading(true);
                        setIsConfirmingPopoverOpen(false);
                        onConfirmingField(options.name, successCallBack);
                    }}
                    onCancel={() => {
                        setIsConfirmingPopoverOpen(false);
                    }}
                    confirmButtonText={<FormattedMessage id="misc.ok" />}
                />
            )}

            {confirmationData && (
                <ConfirmationModal
                    title={options?.confirmation?.title}
                    content={options?.confirmation?.content}
                    onConfirm={() => {
                        setConfirmationData(null);
                        onSubmit(confirmationData);
                    }}
                    onCancel={() => setConfirmationData(null)}
                    confirmButtonText={<FormattedMessage id="misc.ok" />}
                />
            )}
        </TripDetailsSectionWrapper>
    );
}
